import { fetchThunk as fetchAddOnServicesThunk } from '@spike/addon-services-action';
import { Period } from '@spike/model';
import {
    fetchBookingAttemptsMetricsThunk,
    fetchBookingAttemptsThunk
} from 'actions/bookingAttempts/BookingAttemptsActions';
import { fetchServicesThunk } from 'actions/services/ServicesActions';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import styles from 'components/Bookings2/Abandoned/AbandonedBookings.module.scss';
import {
    AbandonedBookingsProvider,
    useAbandonedBookings
} from 'components/Bookings2/Abandoned/AbandonedBookingsProvider';
import { AbandonedBookingsHeader } from 'components/Bookings2/Abandoned/Header/AbandonedBookingsHeader';
import { AbandonedBookingsList } from 'components/Bookings2/Abandoned/List/AbandonedBookingsList';
import { AbandonedBookingsTable } from 'components/Bookings2/Abandoned/Table/AbandonedBookingsTable';
import { OverFullWindow, Spinner } from 'components/UI';
import CreateBookingDrawer from 'components/UI/CreateBookingDrawer';
import { useApiClientWrapper } from 'hooks';
import moment from 'moment-timezone';
import { stepFilterBuckets } from 'model/OnlineBooking';
import Client from 'pages/Client';
import React from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { RootState } from 'store';

export const abandonedBookingsClientFilterOptions = [
    {
        id: 'existing',
        name: 'Existing Clients'
    },
    {
        id: 'lead',
        name: 'Prospects'
    }
];

const AbandonedBookingsContent: React.FC = () => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const { loading: isLoading } = useSelector((state: RootState) => state.bookingAttempts);

    const isMobile = useMediaQuery({ query: '(max-width: 959.95px)' });

    const [showClientForm, setShowClientForm] = React.useState(false);
    const [customerId, setCustomerId] = React.useState<number | undefined>();

    const {
        size,
        page,
        type,
        query,
        selectedSteps,
        selectedClientFilters,
        datePeriod,
        marketplaceIds,
        bookingAttempt,
        showCreateBooking,
        setShowCreateBooking,
        showCreateEvaluation,
        setShowCreateEvaluation,
        isLoading: isLoadingCreateBooking,
        bookingAttemptAppointment,
        closeCreateBookingHandler
    } = useAbandonedBookings();

    const loadBookingAttempts = React.useCallback(
        (
            page = 1,
            size = 10,
            type = 'abandoned',
            query?: string,
            stepBucketIds?: Array<string>,
            clientTypes?: Array<string>,
            datePeriod?: Period,
            marketplaceIds?: Array<number>
        ) => {
            const expandedSteps = stepBucketIds
                ? Array.from(
                      new Set(
                          stepBucketIds.flatMap(id => stepFilterBuckets.find(bucket => bucket.id === id)?.values ?? [])
                      )
                  )
                : undefined;

            dispatch(
                fetchBookingAttemptsThunk(
                    page,
                    size,
                    type,
                    marketplaceIds ?? [],
                    query,
                    expandedSteps,
                    clientTypes,
                    datePeriod
                )
            );
            dispatch(fetchBookingAttemptsMetricsThunk(marketplaceIds ?? [], query, clientTypes, datePeriod));
        },
        [dispatch]
    );

    React.useEffect(() => {
        loadBookingAttempts(
            page,
            size,
            type,
            query ?? undefined,
            selectedSteps,
            selectedClientFilters,
            {
                from: datePeriod.from.clone().startOf('day'),
                to: datePeriod.to.clone().endOf('day')
            },
            marketplaceIds
        );
    }, [page, size, type, query, selectedSteps, selectedClientFilters, datePeriod, marketplaceIds]);

    React.useEffect(() => {
        dispatch(fetchStaffThunk());
        dispatch(fetchServicesThunk());
        dispatch(fetchAddOnServicesThunk(apiClientWrapper));
    }, []);

    const addClientHandler = () => {
        setCustomerId(undefined);
        setShowClientForm(true);
        setShowCreateBooking(false);
        setShowCreateEvaluation(false);
    };

    const addPetHandler = (customerId: number) => {
        setCustomerId(customerId);
        setShowClientForm(true);
        setShowCreateBooking(false);
        setShowCreateEvaluation(false);
    };

    const closeClientFormHandler = (showCreateBooking?: boolean, clientId?: number) => {
        setCustomerId(clientId);
        setShowClientForm(false);
        setShowCreateBooking(showCreateBooking ?? false);
    };

    const closeCreateEvaluationHandler = () => {
        setShowCreateEvaluation(false);
        setCustomerId(undefined);
    };

    const onBookEvaluationHandler = () => {
        setShowCreateEvaluation(false);
        setCustomerId(undefined);
        // Refresh data after successful booking
        loadBookingAttempts(
            page,
            size,
            type,
            query ?? undefined,
            selectedSteps,
            selectedClientFilters,
            {
                from: datePeriod.from.clone().startOf('day'),
                to: datePeriod.to.clone().endOf('day')
            },
            marketplaceIds
        );
    };

    return (
        <div className={styles.root}>
            <AbandonedBookingsHeader />

            {isLoading && <Spinner />}

            {!isLoading && <>{isMobile ? <AbandonedBookingsList /> : <AbandonedBookingsTable />}</>}

            {showCreateBooking && !isLoadingCreateBooking.get(String(bookingAttempt?.id)) && (
                <CreateBookingDrawer
                    parentID={bookingAttempt?.customerId || customerId}
                    at={bookingAttempt?.bookingData.dateTime ?? bookingAttemptAppointment[0]?.variableData[0]?.date}
                    multipleAppointments={bookingAttemptAppointment}
                    staffId={bookingAttempt?.bookingData.staffId}
                    onClose={closeCreateBookingHandler}
                    onBooked={closeCreateBookingHandler}
                    onAddClient={addClientHandler}
                    onAddPet={addPetHandler}
                />
            )}

            {showCreateEvaluation && (
                <CreateBookingDrawer
                    parentID={bookingAttempt?.customerId || customerId}
                    staffId={bookingAttempt?.bookingData.staffId}
                    at={bookingAttempt?.bookingData.dateTime ? moment(bookingAttempt.bookingData.dateTime) : moment()}
                    isEvaluation={true}
                    showTabs={false}
                    onClose={closeCreateEvaluationHandler}
                    onBooked={onBookEvaluationHandler}
                />
            )}

            {showClientForm &&
                createPortal(
                    <OverFullWindow
                        className={styles.overFullWindow}
                        onClose={closeClientFormHandler}
                    >
                        <Client
                            hideHeader
                            isNewBooking
                            clientId={customerId}
                            onClose={closeClientFormHandler}
                            onClientSaved={client => closeClientFormHandler(true, client.id)}
                            onPetSaved={pet => closeClientFormHandler(true, pet.clientId)}
                            onDelete={closeClientFormHandler}
                        />
                    </OverFullWindow>,
                    document.body
                )}
        </div>
    );
};

export const AbandonedBookings: React.FC = () => {
    return (
        <AbandonedBookingsProvider>
            <AbandonedBookingsContent />
        </AbandonedBookingsProvider>
    );
};
